import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { DriveCompareUiService } from '../../drive/drive-compare-ui.service';
import { Brand } from '../../interfaces';
import { BrandService } from '../../services/brand.service';
import { EmitterService } from '../../services/emitter.service';
import {
  MtmBreadcrumbLink,
  MtmBreadcrumbLinkWithCurrentUrl,
  MtmBreadcrumbService,
} from '../../services/mtm-breadcrumb.service';

@Component({
  selector: 'mtm-breadcrumb-component',
  templateUrl: './mtm-breadcrumb.component.html',
  styleUrl: './mtm-breadcrumb.component.scss',
  standalone: false,
})
export class MtmBreadcrumbComponent implements OnInit, OnDestroy {
  private readonly driveCompareUi = inject(DriveCompareUiService);
  protected readonly brandService = inject(BrandService);
  /** Shows the "Compare ×" chip after the crumbs while a drive compare is active. */
  protected readonly driveCompareActive = this.driveCompareUi.compareActive;

  links: MtmBreadcrumbLink[] = [];
  linkForDesktop: MtmBreadcrumbLink[] = [];
  otherLinkForDesktop: MtmBreadcrumbLink[] = [];
  linkForSmall: MtmBreadcrumbLink[] = [];
  otherLinkForSmall: MtmBreadcrumbLink[] = [];

  forCurrentUrl_linkForDesktop: MtmBreadcrumbLinkWithCurrentUrl[] = [];
  ngUnsubscribe = new Subject();

  isReportingV2: boolean = false;

  get shouldShowBackButton(): boolean {
    return this.links.length > 1 && !(this.otherLinkForDesktop.length > 0);
  }

  constructor(
    private readonly router: Router,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService
  ) {
    this.mtmBreadcrumbService.$linkSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (links: MtmBreadcrumbLink[]) => {
        this.links = links;
        this.setBreadcrumbForLayout();
        this.forCurrentUrl_linkForDesktop = this.setCurrentLinksForDesktop(this.linkForDesktop);
      },
      error: (error) => {
        console.log(error);
      },
    });

    EmitterService.get('reportingV2')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          this.isReportingV2 = data;
        },
      });
  }

  appendQueryParams(url: string, queryParams?: { [key: string]: any }): string {
    if (queryParams) {
      const queryString = new URLSearchParams(queryParams).toString();
      return `${url}?${queryString}`;
    }
    return url;
  }

  setCurrentLinksForDesktop(links: MtmBreadcrumbLink[]): MtmBreadcrumbLinkWithCurrentUrl[] {
    return links.map((link) => {
      const linkUrl = Array.isArray(link.url) ? link.url.join('/') : link.url || '';
      const linkWithParams = this.appendQueryParams(linkUrl, link.queryParams);
      // Check if this is the current active URL
      const isCurrentUrl = this.router.url === linkWithParams;
      return {
        ...link,
        isCurrentUrl,
      };
    });
  }

  ngOnInit(): void {
    // Idempotent, and self-guarded against public/review links inside the service.
    this.brandService.loadUserBrands();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  protected closeDriveCompare(): void {
    this.driveCompareUi.exitCompare();
  }

  goToPage(paths: string[], queryParams: any) {
    if (!paths?.length) {
      return;
    }
    this.router.navigate(paths, { queryParams });
  }

  setBreadcrumbForLayout() {
    const desktopSlice = this.links.length - 3 < 0 ? 0 : this.links.length - 3;
    this.linkForDesktop = this.links.slice(desktopSlice, this.links.length);
    if (this.linkForDesktop.length < this.links.length) {
      this.otherLinkForDesktop = this.links.slice(0, this.links.length - this.linkForDesktop.length);
    } else {
      this.otherLinkForDesktop = [];
    }

    const smallSlice = this.links.length - 1 < 0 ? 0 : this.links.length - 1;
    this.linkForSmall = this.links.slice(smallSlice, this.links.length);
    if (this.linkForSmall.length < this.links.length) {
      this.otherLinkForSmall = this.links.slice(0, this.links.length - this.linkForSmall.length);
    } else {
      this.otherLinkForSmall = [];
    }
  }

  /**
   * The first crumb is the brand crumb when ~30 pages hand-build it as { label: brand.name }.
   * It becomes the brand switcher only when the user actually has brands to switch between.
   */
  isBrandCrumb(link: MtmBreadcrumbLink | undefined): boolean {
    if (!link || !this.brandService.canSwitchBrand() || this.links[0]?.label !== link.label) {
      return false;
    }
    // Crumb labels are hand-built from the brand name across ~30 pages, so match leniently
    // (casing/whitespace) against any known brand rather than only the active one.
    const label: string = (link.label ?? '').trim().toLowerCase();
    return !!label && this.brandService.brands().some((b: Brand) => b.name?.trim().toLowerCase() === label);
  }

  selectBrand(brand: Brand): void {
    this.brandService.switchBrand(brand);
  }

  redirectToReportDashboard() {
    this.router.navigate([`/dashboard`]);
  }

  goBack() {
    if (this.links.length > 1) {
      const lastUrl = this.links[this.links.length - 2].url;
      if (lastUrl) {
        this.links = this.links.slice(0, this.links.length - 1);
        this.setBreadcrumbForLayout();
        this.forCurrentUrl_linkForDesktop = this.setCurrentLinksForDesktop(this.linkForDesktop);
        this.router.navigate(lastUrl);
      }
    }
  }
}
